import { useState } from "react";
import { KeyRound, Plus } from "lucide-react";
import { toast } from "sonner";
import { EmptyState, ErrorBanner, PageHeader, SecretReveal, Spinner, StatusBadge, fmt, useLoad } from "../../components/kit";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { api, errMsg } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useT } from "../../lib/i18n";

function CreateKey({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", mode: "test", scopes: ["emails:send", "emails:read"], kind: "api", domain_id: "" });
  const domains = useLoad(() => (open ? api.get("/app/domains").then((r) => r.data.domains) : Promise.resolve([])), [open]);
  const setKind = (kind) => setForm((f) => ({ ...f, kind, scopes: kind === "smtp" ? ["emails:send"] : f.scopes }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const toggle = (s) => setForm((f) => ({ ...f, scopes: f.scopes.includes(s) ? f.scopes.filter((x) => x !== s) : [...f.scopes, s] }));
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError(null);
    try { const { data } = await api.post("/app/api-keys", { ...form, domain_id: form.domain_id || null }); onCreated(data); setForm({ name: "", mode: "test", scopes: ["emails:send", "emails:read"], kind: "api", domain_id: "" }); }
    catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-white/10 bg-[#0F0E13]" data-testid="api-key-create-dialog">
        <DialogHeader><DialogTitle>Create API key</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <ErrorBanner error={error} testId="api-key-create-error" />
          <div className="space-y-1.5"><Label htmlFor="kname">Name</Label><Input id="kname" required maxLength={60} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="api-key-name" /></div>
          <fieldset className="space-y-2"><legend className="mb-1 text-sm font-medium">Use</legend>
            {[["api", "REST API key"], ["smtp", "SMTP credential (password for smtp.csend.net, send only)"]].map(([v, l]) => (
              <label key={v} className="flex items-center gap-2 text-sm text-zinc-300"><input type="radio" name="kind" checked={form.kind === v} onChange={() => setKind(v)} className="accent-purple-600" data-testid={`api-key-kind-${v}`} />{l}</label>
            ))}
          </fieldset>
          <div className="space-y-1.5"><Label htmlFor="kdomain">Only send from domain (optional)</Label>
            <select id="kdomain" value={form.domain_id} onChange={(e) => setForm({ ...form, domain_id: e.target.value })} className="h-10 w-full rounded-xl border border-white/[0.08] bg-white/[0.025] px-3 text-sm text-white" data-testid="api-key-domain">
              <option value="">Any verified domain</option>
              {(domains.data || []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select></div>
          <fieldset className="space-y-2"><legend className="mb-1 text-sm font-medium">Mode</legend>
            {[["test", "Test — validates everything, never sends email"], ["live", "Live — sends real email"]].map(([v, l]) => (
              <label key={v} className="flex items-center gap-2 text-sm text-zinc-300"><input type="radio" name="mode" value={v} checked={form.mode === v} onChange={() => setForm({ ...form, mode: v })} className="accent-purple-600" data-testid={`api-key-mode-${v}`} />{l}</label>
            ))}
          </fieldset>
          <fieldset className="space-y-2"><legend className="mb-1 text-sm font-medium">Scopes</legend>
            {(form.kind === "smtp" ? ["emails:send"] : ["emails:send", "emails:read", "contacts:read", "contacts:write"]).map((s) => (
              <label key={s} className="flex items-center gap-2 font-mono text-xs text-zinc-300"><Checkbox checked={form.scopes.includes(s)} onCheckedChange={() => toggle(s)} data-testid={`api-key-scope-${s.replace(":", "-")}`} />{s}</label>
            ))}
          </fieldset>
          <DialogFooter><Button type="submit" disabled={busy || !form.scopes.length} data-testid="api-key-create-submit">{busy ? "Creating…" : "Create key"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ApiKeys() {
  const { t } = useT();
  const { can, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [secret, setSecret] = useState(null);
  const { data, error, loading, reload } = useLoad(() => api.get("/app/api-keys").then((r) => r.data));
  const revoke = async (k) => {
    if (!window.confirm(`Revoke "${k.name}"? Apps using it will immediately get 401 errors.`)) return;
    try { await api.delete(`/app/api-keys/${k.id}`); toast.success("Key revoked"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader title={t("nav.apikeys")} description={t("desc.apikeys")}
        actions={can("developer") && <Button onClick={() => setOpen(true)} disabled={!user?.email_verified} data-testid="api-key-create-button"><Plus className="mr-1 h-4 w-4" />Create key</Button>} />
      <ErrorBanner error={error} />
      {loading ? <Spinner /> : data && (data.length === 0 ? <EmptyState icon={KeyRound} title="No API keys" body="Create a test key to try the API safely." testId="api-keys-empty" /> : (
        <div className="overflow-x-auto rounded-xl border border-white/10" data-testid="api-keys-table">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead className="bg-white/[0.02] text-xs uppercase tracking-wider text-zinc-500"><tr><th className="p-3">Name</th><th className="p-3">Key</th><th className="p-3">Mode</th><th className="p-3">Scopes</th><th className="p-3">Created</th><th className="p-3">Last used</th><th className="p-3" /></tr></thead>
            <tbody>{data.map((k) => (
              <tr key={k.id} className="border-t border-white/5" data-testid={`api-key-row-${k.id}`}>
                <td className="p-3 text-zinc-200">{k.name}</td>
                <td className="p-3 font-mono text-xs text-zinc-400">{k.prefix}…</td>
                <td className="p-3"><span className={`text-xs ${k.mode === "test" ? "text-fuchsia-300" : "text-emerald-300"}`}>{k.mode}</span></td>
                <td className="p-3 font-mono text-[11px] text-zinc-400">{k.kind === "smtp" && <span className="mr-1 rounded bg-purple-500/15 px-1.5 py-0.5 text-purple-200">SMTP</span>}{k.scopes.join(" ")}{k.allowed_domains?.length > 0 && <div className="text-zinc-500">only {k.allowed_domains.join(", ")}</div>}</td>
                <td className="p-3 text-xs text-zinc-500">{fmt(k.created_at)}</td>
                <td className="p-3 text-xs text-zinc-500">{fmt(k.last_used_at)}</td>
                <td className="p-3 text-right">{k.revoked_at ? <StatusBadge status="revoked" /> : can("developer") &&
                  <Button size="sm" variant="ghost" onClick={() => revoke(k)} className="text-red-300 hover:bg-red-500/10 hover:text-red-200" data-testid={`api-key-revoke-${k.id}`}>Revoke</Button>}</td>
              </tr>))}</tbody>
          </table>
        </div>
      ))}
      <CreateKey open={open} onClose={() => setOpen(false)} onCreated={(k) => { setOpen(false); setSecret(k.key); reload(); }} />
      <SecretReveal open={!!secret} secret={secret} title="Your new API key" onClose={() => setSecret(null)} testId="api-key-reveal" />
    </div>
  );
}
